// import throttle from 'lodash.throttle';
import g from '#core/global';
import events from '#core/events';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';
import hash from '#common/hash';
import masthead from '#partial/masthead/masthead';


const state = window.teal_state = window.teal_state || {};
state.tab_group = state.tab_group || {};

const target_attr = 'aria-controls';
const is_expanded_attr = 'aria-expanded';
const tab_trigger_attr = 'data-tab-trigger';
const tab_hash_attr = 'data-tab-hash';
const default_tab_attr = 'data-tab-default';
const scroll_attr = 'data-tab-group-scroll';
const scroll_delay = 10;

const tab_group_$ = '.tab-group';
const tab_group_menu_$ = '.tab-group__tabs';
const tab_container_$ = '.tab-item';
const tab_button_$ = '.button--tab-toggle';
const tab_content_$ = '.tab-group__content';

let has_tab_group = false;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('tab-group')) return false;
		
		load.content(() => {
			hash.setLinkAttribute(tab_content_$, tab_trigger_attr);
			has_tab_group = assign_elements();
			if (!has_tab_group) return false;
			
			setTimeout(() => {
				activate_from_url();
			}, 100);
		});
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_tab_group) return true;
	
	document.addEventListener(g.click_e, e => {
		const $tab_button = e.target.closest(tab_button_$);
		if (!$tab_button) return;

		e.preventDefault();
		switch_tab($tab_button, {
			update_url: true,
		});
	});
	
	document.addEventListener(g.click_e, e => {
		trigger_tab(e);
	});

	window.addEventListener('hashchange', () => {
		activate_from_url();
	});
	
	return true;
}


/**
 * Switch tab
 * @param {element} $tab
 * @param {object} options
 */
function switch_tab($tab, options = {}) {
	const target_id = $tab.getAttribute(target_attr);
	const $content = document.querySelector(`#${target_id}`);
	if (!$content) return false;

	if (options.update_url) {
		update_url($tab);
	}
	
	// Switch to tab if not already active
	if ($tab.getAttribute(is_expanded_attr) === g.false_s) {
		$tab.setAttribute(is_expanded_attr, g.true_s);
		$content.setAttribute(g.is_active_attr, g.true_s);
		
		const $other_tabs = layout.getAllSiblings($tab.closest(tab_container_$));
		$other_tabs.forEach($other => {
			$other.querySelector(tab_button_$).setAttribute(is_expanded_attr, g.false_s);
		});
		
		const $other_contents = layout.getAllSiblings($content);
		$other_contents.forEach($other => {
			$other.setAttribute(g.is_active_attr, g.false_s);
		});
	}
	
	// Trigger recalculation for reveal sequences after tab switch
	setTimeout(() => {
		window.dispatchEvent(new CustomEvent(events.global.reinitialize));
		window.dispatchEvent(new CustomEvent(events.global.recalculate));
	}, 50);
	
	if (options.scroll === false) return true;

	// Scroll to top of tabs
	const $tab_group = $tab.closest(tab_group_$);
	const $tab_group_menu = $tab.closest(tab_group_menu_$);
	const menu_styles = getComputedStyle($tab_group_menu);
	const group_top = parseInt(menu_styles.top) || 0;
	const position_rect = $tab_group.getBoundingClientRect();
	let position_y = parseInt(position_rect.top) + window.scrollY;
	
	if (masthead.isSticky()) {
		const masthead_height = masthead.getHeight();
		position_y -= masthead_height;
	}
	
	const force_scroll = $tab_group.getAttribute(scroll_attr) === g.true_s;
	const is_below_top = position_rect.top > 0;
	const is_above_threshold = position_rect.top < window.innerHeight * 0.65;
	
	if (
		!force_scroll
		&& is_below_top
		&& is_above_threshold
	) return false;
	
	setTimeout(() => {
		// $tab_group_menu.scrollIntoView({
		// 	block: 'start',
		// 	behavior: 'smooth',
		// });
		
		window.scrollTo({
			top: Math.round(position_y - group_top),
			left: 0,
			behavior: 'smooth',
		});
	}, scroll_delay);
}


/**
 * Check URL for target tab and activate
 */
function activate_from_url() {
	let url_hash = window.location.hash.substring(1);
	try {
		url_hash = decodeURIComponent(url_hash);
	} catch (e) {
		return false;
	}

	if (!url_hash.length) {
		document.querySelectorAll(`${tab_button_$}[${default_tab_attr}="${g.true_s}"]`).forEach($tab => {
			switch_tab($tab, {
				scroll: false,
			});
		});
		return true;
	}
	
	const $target = find_target_tab(url_hash);
	if (!$target) return false;
	
	switch_tab($target, {
		scroll: false,
	});
}


/**
 * Find target tab button
 * @param {string} target_id
 * @return {element}
 */
function find_target_tab(target_id) {
	const selector = [
		`${tab_button_$}[${tab_hash_attr}="${target_id}"]`,
		`${tab_button_$}[${target_attr}="${target_id}"]`,
	].join(', ');
	if (!util.isValidSelector(selector)) return false;
	
	return document.querySelector(selector);
}


/**
 * Add the selected tab's public hash to the browser history
 * @param {element} $tab
 */
function update_url($tab) {
	const tab_hash = $tab.getAttribute(tab_hash_attr);
	if (!tab_hash) return false;

	const new_url = new URL(window.location.href);
	let current_hash = window.location.hash.substring(1);
	try {
		current_hash = decodeURIComponent(current_hash);
	} catch (e) {
		current_hash = '';
	}
	if (
		current_hash === tab_hash
		&& !new_url.searchParams.has('focus_area')
	) return false;

	new_url.searchParams.delete('focus_area');
	new_url.hash = tab_hash;
	window.history.pushState({}, '', `${new_url.pathname}${new_url.search}${new_url.hash}`);
}


/**
 * Trigger tab by remote link
 * @param {event} e
 */
function trigger_tab(e) {
	let $source = e.target;
	$source = $source.closest(`[${tab_trigger_attr}]`);
	if (!$source) return;
	
	e.preventDefault();
	const target_id = $source.getAttribute(tab_trigger_attr);
	if (!target_id) return false;
	
	const $target = find_target_tab(target_id);
	if (!$target) return false;
	
	$target.click();
}
